import { useEffect, useState } from "react";

import { api } from "@/api";

import PowerBIReport from "./PowerBIReport";

const TEST_GROUP_ID = "06dac6f7-de32-487f-aa3f-26cd22b4022b";

interface Report {
  id: string;
  name: string;
}

export default function PowerBiEmbedTestPage() {
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch available reports on mount
  useEffect(() => {
    let isMounted = true;

    const loadReports = async () => {
      try {
        setError(null);
        const fetchedReports = await api.getPowerBiReports(TEST_GROUP_ID);
        if (isMounted) {
          setReports(fetchedReports || []);
          // Auto-select first report
          if (fetchedReports.length > 0) {
            setSelectedReportId(fetchedReports[0].id);
          }
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message || "Failed to load Power BI reports");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadReports();

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedReport = selectedReportId ? reports.find((r) => r.id === selectedReportId) : null;
  // const selectedReport = reports.find((r) => r.id === selectedReportId);

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900">Power BI Embed – Test Page</h1>
          <p className="text-sm text-gray-600">
            Workspace ID:{" "}
            <code className="rounded bg-gray-100 px-2 py-1 font-mono text-xs">{TEST_GROUP_ID}</code>
          </p>
        </div>
      </div>

      {/* Controls Section */}
      <div className="border-b border-gray-200 bg-white px-6 py-4">
        {error && !loading && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-900">Error loading reports</p>
            <p className="text-xs text-red-700 mt-1">{error}</p>
          </div>
        )}

        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Report</label>
            {loading ? (
              <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200" />
            ) : (
              <select
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                value={selectedReportId ?? ""}
                onChange={(e) => setSelectedReportId(e.target.value || null)}
                disabled={reports.length === 0}
              >
                <option value="" disabled>
                  {reports.length === 0 ? "No reports available" : "Choose a report..."}
                </option>
                {reports.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="text-xs text-gray-500">
            {!loading && `${reports.length} report${reports.length !== 1 ? "s" : ""} available`}
          </div>
        </div>
      </div>

      {/* Embed Area */}
      <div className="flex-1 overflow-hidden bg-gray-50 p-6">
        {loading ? (
          // Loading state
          <div className="h-full flex items-center justify-center rounded-lg bg-white border border-gray-200">
            <div className="text-center space-y-3">
              <div className="flex justify-center">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Loading Power BI reports...</p>
                <p className="text-xs text-gray-500 mt-1">
                  Fetching available reports from workspace
                </p>
              </div>
            </div>
          </div>
        ) : selectedReportId && selectedReport ? (
          // Embed the selected report
          <div className="h-full rounded-lg overflow-hidden shadow-sm">
            <div className="h-full bg-white">
              <PowerBIReport
                groupId={TEST_GROUP_ID}
                reportId={selectedReportId}
                reportName={selectedReport.name}
                height="100%"
                width="100%"
              />
            </div>
          </div>
        ) : error ? (
          // Error state
          <div className="h-full flex items-center justify-center rounded-lg border-2 border-dashed border-red-300 bg-red-50">
            <div className="text-center space-y-2">
              <p className="text-sm font-medium text-red-900">Unable to Load Reports</p>
              <p className="text-xs text-red-700">{error}</p>
            </div>
          </div>
        ) : (
          // Empty state
          <div className="h-full flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white">
            <div className="text-center space-y-2">
              <p className="text-sm font-medium text-gray-700">No report selected</p>
              <p className="text-xs text-gray-500">
                Select a report from the dropdown above to view
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
